/**
 * People and access. Who can sign in, as what, and when they last did.
 *
 * Both actions here are real now: `POST /people` creates the account and returns its generated
 * password ONCE, and `POST /people/:id/deactivate` turns an account off and revokes every session
 * it holds. Both are owner-only at the API, so a manager sees the list and disabled buttons with
 * the reason on them rather than live buttons that 403.
 *
 * The password is shown exactly once, in a panel that says so. There is no password-reset flow and
 * `UNIQUE(tenant_id,email)` blocks the obvious workaround of re-adding the person — which is
 * written on the panel, because the moment to learn it is before the dialog closes, not after.
 *
 * Author: Danish Khan
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { api } from '../lib/api.ts';
import { formatAgo, formatDateTime } from '../lib/format.ts';
import { canChangePeople, canManagePeople, roleGate } from '../lib/pii.ts';
import { useUser } from '../lib/session.tsx';
import type { Role, User } from '../lib/types.ts';
import { Button } from '../components/Button.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { Drawer } from '../components/Drawer.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import { Pill } from '../components/Pill.tsx';
import { useToast } from '../components/Toast.tsx';

const column = createColumnHelper<User>();

const ROLE_NOTE: Record<Role, string> = {
  owner: 'everything, including People',
  manager: 'everything except billing',
  rep: 'inbox, handoffs, pipeline — no Explain, no cost',
  ops: 'Explain and health — phone numbers redacted',
};

const ROLES: Role[] = ['owner', 'manager', 'rep', 'ops'];

export function PeoplePage(): ReactNode {
  const user = useUser();
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();
  const people = useQuery({ queryKey: ['people'], queryFn: () => api.people(), enabled: canManagePeople(user.role) });

  const mayChange = canChangePeople(user.role);
  const gate = roleGate(mayChange, 'Adding and deactivating accounts is owner-only', user.role);

  const [inviting, setInviting] = useState(false);
  const [created, setCreated] = useState<{ user: User; password: string } | null>(null);

  const add = useMutation({
    mutationFn: (input: { email: string; name: string; role: Role }) => api.addPerson(input),
    onSuccess: (result) => {
      setCreated(result);
      setInviting(false);
      void queryClient.invalidateQueries({ queryKey: ['people'] });
    },
    onError: (err) => toastError('create that account', err),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.deactivatePerson(id),
    onSuccess: (u) => {
      toast(`${u.name} can no longer sign in`, { tone: 'ok', body: 'Every session that account held has been revoked.' });
      void queryClient.invalidateQueries({ queryKey: ['people'] });
    },
    onError: (err) => toastError('deactivate that account', err),
  });

  const columns = useMemo(
    () => [
      column.accessor('name', {
        header: 'Name',
        cell: (ctx) => (
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-fg">{ctx.getValue()}</p>
            <p className="truncate font-mono text-[11px] text-fg-subtle">{ctx.row.original.email}</p>
          </div>
        ),
      }),
      column.accessor('role', {
        header: 'Role',
        meta: { width: '260px' },
        cell: (ctx) => (
          <div className="flex items-center gap-2">
            <Pill tone={ctx.getValue() === 'owner' ? 'brand' : 'neutral'}>{ctx.getValue()}</Pill>
            <span className="truncate text-[11px] text-fg-subtle">{ROLE_NOTE[ctx.getValue()]}</span>
          </div>
        ),
      }),
      column.accessor('active', {
        header: 'Status',
        meta: { width: '110px' },
        cell: (ctx) => (ctx.getValue() ? <Pill tone="ok">active</Pill> : <Pill tone="danger">deactivated</Pill>),
      }),
      column.accessor('lastLoginAt', {
        header: 'Last sign-in',
        meta: { width: '150px' },
        cell: (ctx) => {
          const v = ctx.getValue();
          return (
            <span className="text-xs text-fg-muted" title={v ? formatDateTime(v) : undefined}>
              {v ? formatAgo(v) : 'never'}
            </span>
          );
        },
      }),
      column.display({
        id: 'actions',
        header: '',
        meta: { width: '160px' },
        cell: (ctx) => {
          const row = ctx.row.original;
          // Deactivating your own account is how a tenant ends up with no owner. The API does not
          // stop it; this does, and says why.
          const isSelf = row.id === user.id;
          const reason = !row.active
            ? 'This account is already deactivated.'
            : isSelf
              ? 'You cannot deactivate the account you are signed in with.'
              : gate;
          return (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="secondary"
                disabled={Boolean(reason) || deactivate.isPending}
                title={reason}
                onClick={() => deactivate.mutate(row.id)}
              >
                Deactivate
              </Button>
            </div>
          );
        },
      }),
    ],
    [deactivate, gate, user.id],
  );

  if (!canManagePeople(user.role)) {
    return (
      <EmptyState
        title="People is for owners and managers"
        body={`You are signed in as ${user.role}. Ask an owner if you need to change who has access.`}
      />
    );
  }

  if (people.isError) {
    return <ErrorState error={people.error} context="load the team" onRetry={() => void people.refetch()} />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-3 border-b border-line bg-surface px-4 py-2.5">
        <p className="text-xs text-fg-muted">{people.data?.length ?? 0} accounts</p>
        {gate && <p className="text-[11px] text-fg-subtle">{gate}</p>}
        <Button size="sm" className="ml-auto" disabled={!mayChange} title={gate} onClick={() => setInviting(true)}>
          Add someone
        </Button>
      </div>
      <div className="min-h-0 flex-1">
        <DataTable
          data={people.data ?? []}
          loading={people.isLoading}
          columns={columns}
          rowKey={(u) => u.id}
          ariaLabel="People and access"
          emptyTitle="No accounts"
        />
      </div>

      <Drawer
        open={inviting}
        onOpenChange={(open) => !open && setInviting(false)}
        title="Add someone to this tenant"
        description="They get a generated password, shown once. There is no reset flow yet."
      >
        <InviteForm pending={add.isPending} onSubmit={(input) => add.mutate(input)} />
      </Drawer>

      <Drawer
        open={Boolean(created)}
        onOpenChange={(open) => !open && setCreated(null)}
        title="Account created"
        description="Copy the password now — it is not stored anywhere readable and cannot be shown again."
      >
        {created && (
          <div className="flex flex-col gap-3">
            <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-fg-subtle">Name</dt>
              <dd className="text-fg">{created.user.name}</dd>
              <dt className="text-fg-subtle">Email</dt>
              <dd className="font-mono text-xs text-fg">{created.user.email}</dd>
              <dt className="text-fg-subtle">Role</dt>
              <dd>
                <Pill tone="neutral">{created.user.role}</Pill>
              </dd>
            </dl>
            <div className="rounded-[var(--h1-radius)] border border-brand bg-brand-soft p-3">
              <p className="text-[11px] font-semibold tracking-wide text-brand-soft-fg uppercase">Password — shown once</p>
              <p className="mt-1.5 font-mono text-base break-all text-brand-soft-fg select-all">{created.password}</p>
            </div>
            <p className="text-[11px] text-fg-subtle">
              There is no password-reset flow yet, and one address can hold only one account per tenant — so if this
              is lost, the account cannot simply be re-added under the same email.
            </p>
            <Button variant="secondary" onClick={() => setCreated(null)}>
              I have copied it
            </Button>
          </div>
        )}
      </Drawer>
    </div>
  );
}

function InviteForm({
  pending,
  onSubmit,
}: {
  pending: boolean;
  onSubmit: (input: { email: string; name: string; role: Role }) => void;
}): ReactNode {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('rep');

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) return;
    onSubmit({ email: email.trim(), name: name.trim(), role });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-fg">Name</span>
        {/* No pattern on either field. Every alphabet is a real name somewhere, and the API's own
            email check is structural rather than an ASCII regex for exactly that reason. */}
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="h-9 rounded-[var(--h1-radius)] border border-line-strong bg-bg px-3 text-sm text-fg"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-fg">Email</span>
        <input
          type="text"
          inputMode="email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="h-9 rounded-[var(--h1-radius)] border border-line-strong bg-bg px-3 font-mono text-sm text-fg"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-fg">Role</span>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          className="h-9 rounded-[var(--h1-radius)] border border-line-strong bg-bg px-2 text-sm text-fg"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r} — {ROLE_NOTE[r]}
            </option>
          ))}
        </select>
      </label>
      <Button type="submit" disabled={pending || !email.trim() || !name.trim()}>
        {pending ? 'Creating…' : 'Create account'}
      </Button>
      <p className="text-[11px] text-fg-subtle">
        <strong className="text-fg-muted">ops</strong> is deliberately below <strong className="text-fg-muted">rep</strong>:
        an operator runs the system and does not inherit the client's customer phone numbers.
      </p>
    </form>
  );
}
